import React, { useMemo } from 'react';
import { paiVaccines, type Vaccine } from '../../../../data/pediatric';
import { getVaccinesForAge } from '../../../../utils/pediatricAgeGroup';

interface VaccineEntry {
  vaccine: string;
  dose: string;
  applied: boolean;
  date: string | null;
}

interface Props {
  vaccines: VaccineEntry[];
  vaccination_status: string;
  vaccination_observations: string;
  setPediatricField: (section: string, field: string, value: any) => void;
  patientBirthDate: string | null;
}

function getAgeInMonths(birthDate: string): number {
  const birth = new Date(birthDate);
  const now = new Date();
  let months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
  if (now.getDate() < birth.getDate()) months--;
  return Math.max(0, months);
}

function recalcStatus(vaccineEntries: VaccineEntry[]): string {
  const total = vaccineEntries.length;
  if (total === 0) return 'no_iniciado';
  const applied = vaccineEntries.filter((v) => v.applied).length;
  if (applied === total) return 'completo';
  if (applied > 0) return 'incompleto';
  return 'no_iniciado';
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  completo: { label: 'Completo', color: '#10b981', bg: '#f0fdf4' },
  incompleto: { label: 'Incompleto', color: '#f59e0b', bg: '#fffbeb' },
  no_iniciado: { label: 'No iniciado', color: '#6b7280', bg: '#f3f4f6' },
};

const VaccinationSchedule: React.FC<Props> = ({
  vaccines,
  vaccination_status,
  vaccination_observations,
  setPediatricField,
  patientBirthDate,
}) => {
  const expected = useMemo(() => {
    if (!patientBirthDate) return [];
    const ageMonths = getAgeInMonths(patientBirthDate);
    return getVaccinesForAge(ageMonths, paiVaccines);
  }, [patientBirthDate]);

  if (!patientBirthDate) {
    return (
      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Esquema de Vacunación (PAI)</h5>
          <p className="text-muted small mt-2 mb-0">
            <i className="fa fa-info-circle me-1"></i>
            Seleccione un paciente con fecha de nacimiento para ver las vacunas esperadas.
          </p>
        </div>
      </div>
    );
  }

  const handleToggle = (idx: number, applied: boolean) => {
    const updated = vaccines.map((v, i) =>
      i === idx ? { ...v, applied, date: applied ? v.date : null } : v
    );
    setPediatricField('vaccines', '', updated);
    setPediatricField('vaccination_status', '', recalcStatus(updated));
  };

  const handleDateChange = (idx: number, date: string) => {
    const updated = vaccines.map((v, i) =>
      i === idx ? { ...v, date } : v
    );
    setPediatricField('vaccines', '', updated);
  };

  const getEntry = (vaccine: Vaccine, idx: number): VaccineEntry | undefined => {
    return vaccines.find(
      (v) => v.vaccine === vaccine.vaccine && v.dose === vaccine.dose
    );
  };

  const ageMonths = getAgeInMonths(patientBirthDate);
  const status = STATUS_CONFIG[vaccination_status] || STATUS_CONFIG.no_iniciado;

  const groupedByVaccine: Record<string, Vaccine[]> = {};
  expected.forEach((v) => {
    if (!groupedByVaccine[v.vaccine]) groupedByVaccine[v.vaccine] = [];
    groupedByVaccine[v.vaccine].push(v);
  });

  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="card-title mb-0">
            <i className="fa fa-medkit me-1"></i>
            Esquema de Vacunación (PAI)
          </h5>
          <div
            style={{
              display: 'inline-block',
              padding: '3px 12px',
              borderRadius: 4,
              fontSize: 13,
              fontWeight: 600,
              color: status.color,
              background: status.bg,
              border: `1px solid ${status.color}40`,
            }}
          >
            {status.label} · {ageMonths} meses
          </div>
        </div>

        {Object.entries(groupedByVaccine).map(([vaccineName, doses]) => (
          <div key={vaccineName} className="mb-3">
            <h6 className="text-muted fw-semibold">{vaccineName}</h6>
            <div className="table-responsive">
              <table className="table table-sm table-borderless mb-0">
                <tbody>
                  {doses.map((dose) => {
                    const existingIdx = vaccines.findIndex(
                      (v) => v.vaccine === dose.vaccine && v.dose === dose.dose
                    );
                    const entry = existingIdx >= 0 ? vaccines[existingIdx] : null;

                    return (
                      <tr key={`${dose.vaccine}-${dose.dose}`}>
                        <td style={{ width: 40 }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`vac-${dose.vaccine}-${dose.dose}`}
                            checked={entry?.applied ?? false}
                            onChange={(e) => {
                              if (existingIdx >= 0) {
                                handleToggle(existingIdx, e.target.checked);
                              } else {
                                const updated = [
                                  ...vaccines,
                                  { vaccine: dose.vaccine, dose: dose.dose, applied: e.target.checked, date: null },
                                ];
                                setPediatricField('vaccines', '', updated);
                                setPediatricField('vaccination_status', '', recalcStatus(updated));
                              }
                            }}
                          />
                        </td>
                        <td>
                          <label
                            className="form-check-label"
                            htmlFor={`vac-${dose.vaccine}-${dose.dose}`}
                          >
                            {dose.dose}
                          </label>
                        </td>
                        <td className="text-muted small">{dose.description}</td>
                        <td className="text-muted small">({dose.age_months} meses)</td>
                        <td style={{ width: 180 }}>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={entry?.date ?? ''}
                            disabled={!entry?.applied}
                            onChange={(e) => {
                              if (existingIdx >= 0) {
                                handleDateChange(existingIdx, e.target.value);
                              }
                            }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        <div className="row mt-2">
          <div className="col-md-8">
            <div className="mb-3">
              <label className="form-label">Observaciones de Vacunación</label>
              <textarea
                className="form-control"
                rows={2}
                value={vaccination_observations}
                onChange={(e) => setPediatricField('vaccination_observations', '', e.target.value)}
                placeholder="Reacciones adversas, esquemas atrasados, referencias a vacunatorio..."
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VaccinationSchedule;
